import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import {
  ApiWebSocket,
  WSNotification,
} from '@paradigm/api/src/internal/createWebSocket';
import { AsyncResp, SuccessResp } from '@paradigm/api/src/fetch-api';
import { SubscriptionState } from '#/positions/contexts/abstractions/useSubscriber';

export type FetcherStatus = 'ERROR' | 'LOADING' | 'SYNCED';

export interface FetcherState {
  error: string;
  status: FetcherStatus;
}

const INITIAL_STATE: FetcherState = {
  error: '',
  status: 'LOADING',
};

/**
 * @param ws concrete websocket instance object
 * @param wsChannel websocket channel meant to filter messages from
 * @param wsSubscriptionsStatus subscriptions status that indicates when start reconciling snapshot and ws notification state
 * @param applySnapshot function to apply snapshot to update store state from Entity desired
 * @param applyWSNotification function to dispatch delta norifications messages update state to store
 * @param fetchSnapshot async function to fetch snapshot from
 * @param T generic type of the websocket that extends ApiWebSocket
 * @param N generic type of the websocket notification that extends WSNotification
 * @param E generic type of the Entity fetched
 * @returns {FetcherState}
 */
export function useFetcher<
  T extends ApiWebSocket<N>,
  N extends Pick<WSNotification, 'params'>,
  E,
>(
  ws: T,
  wsChannel: string,
  wsSubscriptionsStatus: SubscriptionState['status'],
  applySnapshot: (result: SuccessResp<E>) => void,
  applyWSNotification: (notif: N) => void,
  fetchSnapshot: () => AsyncResp<E>,
): FetcherState {
  const [state, setState] = useState<FetcherState>(INITIAL_STATE);

  useEffect(() => {
    let clearQueuingHandler = noop;
    let clearNotificationHandler = noop;
    const queue: N[] = [];
    const startQueuingNotifications = () => {
      clearQueuingHandler = ws.addNotificationHandler((notif) => {
        if (!isNotificationFromChannel<N>(wsChannel, notif)) return;
        queue.push(notif);
      });
    };
    const startApplyingNotifications = () => {
      clearNotificationHandler = ws.addNotificationHandler((notif) => {
        if (!isNotificationFromChannel<N>(wsChannel, notif)) return;
        applyWSNotification(notif);
      });
    };

    const sync = async () => {
      startQueuingNotifications();
      const resp = await fetchSnapshot();
      if (!resp.ok) {
        throw new Error(resp.error);
      }
      applySnapshot(resp);
      clearQueuingHandler();
      queue.forEach(applyWSNotification);
      startApplyingNotifications();
    };

    // WIP: uncomment when WS endpoint implemented
    if (wsSubscriptionsStatus === 'SUBSCRIBED') {
      sync()
        .then(() => {
          setState({ error: '', status: 'SYNCED' });
        })
        .catch((error: Error) =>
          setState({
            error: `Failed to fetch positions: ${error.message}`,
            status: 'ERROR',
          }),
        );
    }

    return () => {
      clearQueuingHandler();
      clearNotificationHandler();
    };
  }, [
    ws,
    wsSubscriptionsStatus,
    applySnapshot,
    applyWSNotification,
    fetchSnapshot,
    wsChannel,
  ]);

  return state;
}

function isNotificationFromChannel<T extends Pick<WSNotification, 'params'>>(
  channel: string,
  notification: T,
) {
  return notification.params.channel === channel;
}
